// Implements: F-USG-006
/**
 * What the current version cost, as one fact of the drawer's "Detalles" (mockup v3 P7, v3
 * `detcost`): "Coste de la v3 · USD 0.12 · 3 min", or "Sin coste · restaurada" for a version no
 * run produced. Shown only to whoever may edit the mockup (P7-1, `useEditorVersionCosts`).
 *
 * The version's number comes from the tree the chip already read (the shared `versionTreeKey`), the
 * cost from the chip's own version-costs query (the same ids, the same key): the drawer adds no
 * request. Until both are known, or when either is refused, it draws nothing: the other details never wait on it.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { versionTreeKey } from "../../../shared/api/queryKeys";
import { useEditorVersionCosts } from "../../../shared/api/versionCosts";
import { fetchVersionNodes } from "../../../shared/api/versionTree";
import { useTranslation } from "../../../shared/i18n";
import { costLabel } from "../../../shared/ui/costText";
import type { VersionTreeNode } from "../../../types/api";
import styles from "./VersionCostFactContainer.module.css";

export interface VersionCostFactContainerProps {
  readonly mockupId: string;
  /** The version whose cost is shown: `null` when the mockup has none, `undefined` while unknown. */
  readonly versionId: string | null | undefined;
}

const NO_IDS: readonly string[] = [];

export function VersionCostFactContainer({ mockupId, versionId }: VersionCostFactContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const known = typeof versionId === "string";
  const nodes = useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchVersionNodes(mockupId),
    enabled: known,
  });
  // The chip's very request (every version of the tree; the shared key sorts them): the drawer
  // reads its cached answer instead of asking again.
  const ids = useMemo(() => (known ? (nodes.data?.map((node) => node.id) ?? NO_IDS) : NO_IDS), [known, nodes.data]);
  const costs = useEditorVersionCosts(mockupId, ids);

  if (!known) return null;
  const node = nodes.data?.find((entry) => entry.id === versionId);
  const label = costLabel(costs.costs?.get(versionId), "chip");
  if (node === undefined || label === null) return null;
  return (
    <dl className={styles.fact} data-version-cost="">
      <div className={styles.row}>
        <dt>{t("cost.detailsTerm", { n: node.sequence_number })}</dt>
        <dd>{label}</dd>
      </div>
    </dl>
  );
}
